// Escala del diseño, como el panel «Scale» de Bambu Studio: el cliente estira el archivo en X y en Y
// (1 = 100 %) y todo lo que sigue —tiempos, piezas, nesting, cama, hoja— trabaja sobre el diseño ya estirado.
import { ErrorCotizacion } from './errores';
import type { Analisis, Capa, Pieza, Polilinea, Punto, Raster } from './types';

export const ESCALA_MIN = 0.01;   // 1 %
export const ESCALA_MAX = 100;    // 10 000 %

function validarEscala(valor: number | undefined, eje: 'X' | 'Y'): number {
  const e = valor ?? 1;
  if (!Number.isFinite(e) || e < ESCALA_MIN || e > ESCALA_MAX) {
    throw new ErrorCotizacion('otro',
      `La escala en ${eje} debe ser un número entre ${ESCALA_MIN * 100} % y ${ESCALA_MAX * 100} %.`);
  }
  return e;
}

function escalarPuntos(puntos: Punto[] | undefined, sx: number, sy: number): Punto[] {
  return (puntos ?? []).map(([x, y]) => [x * sx, y * sy] as Punto);
}

/** Longitud real de una polilínea, segmento por segmento (cerrada = también el segmento de cierre). */
export function longitudPolilinea(p: Polilinea): number {
  const puntos = p.puntos ?? [];
  let total = 0;
  for (let i = 1; i < puntos.length; i++) {
    total += Math.hypot(puntos[i][0] - puntos[i - 1][0], puntos[i][1] - puntos[i - 1][1]);
  }
  if (p.cerrada && puntos.length > 2) {
    const a = puntos[puntos.length - 1], b = puntos[0];
    total += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return total;
}

function escalarCapa(c: Capa, sx: number, sy: number, uniforme: boolean, medio: number): Capa {
  const polilineas = (c.polilineas ?? []).map((p) => ({ ...p, puntos: escalarPuntos(p.puntos, sx, sy) }));
  let longitudMm: number;
  if (uniforme) {
    // Con escala uniforme cada segmento mide exactamente `sx` veces más: el atajo da el mismo número
    // que recalcular la geometría (lo comprueba una prueba).
    longitudMm = c.longitudMm * sx;
  } else if (polilineas.length > 0) {
    // Escala no uniforme: multiplicar la longitud es incorrecto (una diagonal no crece como un lado),
    // así que se vuelve a medir sobre las polilíneas ya estiradas.
    longitudMm = polilineas.reduce((s, p) => s + longitudPolilinea(p), 0);
  } else {
    // Capa sin geometría (sólo trae la longitud): sin dirección que medir, media geométrica.
    longitudMm = c.longitudMm * medio;
  }
  return { ...c, longitudMm, areaMm2: c.areaMm2 * sx * sy, polilineas };
}

function escalarPieza(p: Pieza, sx: number, sy: number): Pieza {
  return {
    ...p,
    exterior: escalarPuntos(p.exterior, sx, sy),
    agujeros: (p.agujeros ?? []).map((a) => escalarPuntos(a, sx, sy)),
    anchoMm: p.anchoMm * sx,
    altoMm: p.altoMm * sy,
    areaMm2: p.areaMm2 * sx * sy,
  };
}

function escalarRaster(r: Raster, sx: number, sy: number, medio: number): Raster {
  // Los mismos píxeles ocupan más milímetros, así que la resolución efectiva baja.
  return { ...r, x: r.x * sx, y: r.y * sy, anchoMm: r.anchoMm * sx, altoMm: r.altoMm * sy, dpi: r.dpi / medio };
}

/**
 * Devuelve el análisis como si el diseño estuviera estirado `escalaX` × `escalaY` (1 = 100 %).
 * Con ambas escalas en 1 devuelve el mismo objeto (===) para no trabajar de más.
 * Lanza `ErrorCotizacion('otro')` si alguna escala no es finita o se sale de 0.01–100.
 */
export function escalarAnalisis(analisis: Analisis, escalaX = 1, escalaY = 1): Analisis {
  const sx = validarEscala(escalaX, 'X');
  const sy = validarEscala(escalaY, 'Y');
  if (sx === 1 && sy === 1) return analisis;

  const uniforme = sx === sy;
  const medio = Math.sqrt(sx * sy); // factor sin eje: dpi y capas sin polilíneas

  return {
    ...analisis,
    anchoMm: analisis.anchoMm * sx,
    altoMm: analisis.altoMm * sy,
    capas: analisis.capas.map((c) => escalarCapa(c, sx, sy, uniforme, medio)),
    rasters: analisis.rasters.map((r) => escalarRaster(r, sx, sy, medio)),
    piezas: analisis.piezas.map((p) => escalarPieza(p, sx, sy)),
  };
}
